import type {
  Benchmark,
  BenchmarkId,
  Claim,
  Comparability,
  Lab,
  Model,
  Price,
  Source,
} from "../types";
import {
  grok45NewClaimSeeds,
  kimiK3ClaimSeeds,
  latestBenchmarkDefinitions,
  type ClaimSeed,
} from "./releaseCharts";

export const dataThrough = "2026-07-26";
export const coverageStart = "2026-04-23";

export const labs: Lab[] = [
  { id: "openai", name: "OpenAI", shortName: "OpenAI", website: "https://openai.com/" },
  { id: "anthropic", name: "Anthropic", shortName: "Anthropic", website: "https://www.anthropic.com/" },
  { id: "google", name: "Google DeepMind", shortName: "Google", website: "https://deepmind.google/" },
  { id: "spacexai", name: "SpaceXAI", shortName: "SpaceXAI", website: "https://x.ai/" },
  { id: "kimi", name: "Moonshot AI / Kimi", shortName: "Kimi", website: "https://www.kimi.com/" },
  { id: "zai", name: "Z.ai", shortName: "Z.ai", website: "https://z.ai/" },
  { id: "qwen", name: "Qwen", shortName: "Qwen", website: "https://qwen.ai/" },
  { id: "minimax", name: "MiniMax", shortName: "MiniMax", website: "https://www.minimax.io/" },
  { id: "deepseek", name: "DeepSeek", shortName: "DeepSeek", website: "https://www.deepseek.com/" },
];

export const models: Model[] = [
  { id: "kimi-k3", name: "Kimi K3", ownerId: "kimi", releaseDate: "2026-07-16" },
  { id: "grok-4-5", name: "Grok 4.5", ownerId: "spacexai", releaseDate: "2026-07-16" },
  { id: "gpt-5-6-sol", name: "GPT-5.6 Sol", ownerId: "openai", releaseDate: "2026-07-09" },
  { id: "fable-5", name: "Claude Fable 5", ownerId: "anthropic", releaseDate: "2026-06-09" },
  { id: "gpt-5-5", name: "GPT-5.5", ownerId: "openai", releaseDate: "2026-04-23" },
  { id: "gpt-5-4", name: "GPT-5.4", ownerId: "openai" },
  { id: "opus-4-8", name: "Claude Opus 4.8", ownerId: "anthropic" },
  { id: "opus-4-7", name: "Claude Opus 4.7", ownerId: "anthropic" },
  { id: "glm-5-2", name: "GLM-5.2", ownerId: "zai", releaseDate: "2026-06-16", openWeights: true },
  { id: "glm-5-1", name: "GLM-5.1", ownerId: "zai", openWeights: true },
  { id: "gemini-3-1-pro", name: "Gemini 3.1 Pro", ownerId: "google" },
  { id: "qwen-3-7-max", name: "Qwen3.7-Max", ownerId: "qwen" },
  { id: "minimax-m3", name: "MiniMax M3", ownerId: "minimax" },
  { id: "deepseek-v4-pro", name: "DeepSeek-V4-Pro", ownerId: "deepseek" },
];

export const benchmarks: Benchmark[] = [
  {
    id: "deep-swe",
    name: "DeepSWE",
    shortName: "DeepSWE",
    category: "Coding",
    metric: "resolve rate",
    unit: "%",
    direction: "higher",
    description: "Agentic software-engineering tasks evaluated with mini-swe-agent-family harnesses.",
  },
  {
    id: "swe-bench-pro",
    name: "SWE-Bench Pro",
    shortName: "SWE-Bench Pro",
    category: "Coding",
    metric: "resolve rate",
    unit: "%",
    direction: "higher",
    description: "Professional repository issues; reported values can use different scaffolds and harnesses.",
  },
  {
    id: "terminal-bench-2-1",
    name: "Terminal-Bench 2.1",
    shortName: "Terminal-Bench 2.1",
    category: "Coding",
    metric: "accuracy",
    unit: "%",
    direction: "higher",
    description: "Terminal-agent tasks. This view uses each report’s best-reported harness column.",
  },
  {
    id: "swe-marathon",
    name: "SWE-Marathon",
    shortName: "SWE-Marathon",
    category: "Coding",
    metric: "pass@1",
    unit: "%",
    direction: "higher",
    description: "Long-horizon software engineering tasks including compilers, kernels, and production systems.",
  },
  {
    id: "hle",
    name: "Humanity’s Last Exam",
    shortName: "HLE",
    category: "Reasoning",
    metric: "accuracy",
    unit: "%",
    direction: "higher",
    description: "Broad expert-level reasoning. Z.ai defaults to the text-only subset; starred values use the full set.",
  },
  {
    id: "hle-tools",
    name: "Humanity’s Last Exam with tools",
    shortName: "HLE + tools",
    category: "Reasoning",
    metric: "accuracy",
    unit: "%",
    direction: "higher",
    description: "HLE with tool access. Tool stack and context strategy materially affect comparability.",
  },
  ...latestBenchmarkDefinitions,
];

export const sources: Source[] = [
  {
    id: "xai-grok-45",
    publisherId: "spacexai",
    title: "Introducing Grok 4.5",
    url: "https://x.ai/news/grok-4-5",
    publishedAt: "2026-07-16",
    kind: "Release post",
    protocol: "A release chart combining runs and best-published competitor figures.",
    caveat: "Competitor figures are drawn from developer system cards or benchmark leaderboards.",
  },
  {
    id: "kimi-k3-release",
    publisherId: "kimi",
    title: "Kimi K3: Open Frontier Intelligence",
    url: "https://www.kimi.com/blog/kimi-k3",
    publishedAt: "2026-07-16",
    kind: "Release post",
    protocol: "Kimi K3 at max reasoning effort; row-specific KimiCode, Claude Code, Codex, leaderboard, and cited-source methods.",
    caveat: "Fable 5 rows may include fallback behavior; GPT-5.6 Sol rows may include cyberguard effects. Several competitor values are cited rather than rerun.",
  },
  {
    id: "zai-glm-52",
    publisherId: "zai",
    title: "GLM-5.2: Built for Long-Horizon Tasks",
    url: "https://z.ai/blog/glm-5.2",
    publishedAt: "2026-06-16",
    kind: "Release post",
    protocol: "Full benchmark table with source-specific prompts, harnesses, limits, and footnotes.",
    caveat: "HLE stars denote full-set rather than text-only scores; coding harnesses vary by row.",
  },
  {
    id: "openai-gpt-55",
    publisherId: "openai",
    title: "Introducing GPT-5.5",
    url: "https://openai.com/index/introducing-gpt-5-5/",
    publishedAt: "2026-04-23",
    kind: "Release post",
    protocol: "OpenAI’s published release comparison table.",
    caveat: "SWE-Bench Pro carries a memorization-evidence warning in the source.",
  },
  {
    id: "openai-gpt-55-pricing",
    publisherId: "openai",
    title: "GPT-5.5 model pricing",
    url: "https://developers.openai.com/api/docs/models/gpt-5.5",
    publishedAt: dataThrough,
    kind: "Pricing",
    caveat: "Prompts above 272K input tokens use higher long-context rates.",
  },
  {
    id: "anthropic-pricing",
    publisherId: "anthropic",
    title: "Claude Platform pricing",
    url: "https://platform.claude.com/docs/en/about-claude/pricing",
    publishedAt: dataThrough,
    kind: "Pricing",
    caveat: "Cache-write and cache-read prices are separate dimensions.",
  },
  {
    id: "google-pricing",
    publisherId: "google",
    title: "Gemini Developer API pricing",
    url: "https://ai.google.dev/gemini-api/docs/pricing",
    publishedAt: "2026-06-18",
    kind: "Pricing",
    caveat: "Listed Gemini 3.1 Pro rates apply to prompts at or below 200K tokens.",
  },
  {
    id: "xai-pricing",
    publisherId: "spacexai",
    title: "SpaceXAI API pricing",
    url: "https://docs.x.ai/developers/pricing",
    publishedAt: dataThrough,
    kind: "Pricing",
    caveat: "Grok 4.5 requests at or above 200K tokens use a higher long-context tier.",
  },
];

type ScoreRow = readonly [modelId: string, score: number, note?: string];

const claimSet = (
  sourceId: string,
  benchmarkId: BenchmarkId,
  locator: string,
  comparability: Comparability,
  rows: readonly ScoreRow[],
): Claim[] =>
  rows.map(([modelId, score, note]) => ({
    id: `${sourceId}-${benchmarkId}-${modelId}`,
    modelId,
    benchmarkId,
    score,
    sourceId,
    displayValue: `${score}${note?.includes("Full-set") ? "*" : ""}`,
    locator,
    comparability,
    note,
  }));

const seededClaimSet = (sourceId: string, seeds: ClaimSeed[]): Claim[] =>
  seeds.map((seed) => ({
    id: `${sourceId}-${seed.benchmarkId}-${seed.modelId}`,
    modelId: seed.modelId,
    benchmarkId: seed.benchmarkId,
    score: seed.score,
    sourceId,
    displayValue: seed.displayValue,
    locator: seed.locator,
    comparability: seed.comparability,
    note: seed.note,
  }));

export const claims: Claim[] = [
  ...seededClaimSet("xai-grok-45", grok45NewClaimSeeds),
  ...seededClaimSet("kimi-k3-release", kimiK3ClaimSeeds),
  ...claimSet("xai-grok-45", "deep-swe", "DeepSWE 1.1 chart", "method-sensitive", [
    ["fable-5", 70],
    ["gpt-5-5", 67],
    ["opus-4-8", 59],
    ["grok-4-5", 53],
    ["glm-5-2", 44],
  ]),
  ...claimSet("zai-glm-52", "deep-swe", "Full benchmark table · Coding · DeepSWE", "method-sensitive", [
    ["glm-5-2", 46.2],
    ["glm-5-1", 18],
    ["qwen-3-7-max", 18],
    ["minimax-m3", 20],
    ["deepseek-v4-pro", 8],
    ["opus-4-8", 58],
    ["gpt-5-5", 70],
    ["gemini-3-1-pro", 10],
  ]),
  ...claimSet("xai-grok-45", "swe-bench-pro", "SWE Bench Pro chart", "reported-best", [
    ["fable-5", 80.4],
    ["opus-4-8", 69.2],
    ["grok-4-5", 64.7],
    ["opus-4-7", 64.3],
    ["glm-5-2", 62.1],
    ["gpt-5-5", 58.6],
  ]),
  ...claimSet("zai-glm-52", "swe-bench-pro", "Full benchmark table · Coding · SWE-bench Pro", "same-table", [
    ["glm-5-2", 62.1],
    ["glm-5-1", 58.4],
    ["qwen-3-7-max", 60.6],
    ["minimax-m3", 59],
    ["deepseek-v4-pro", 55.4],
    ["opus-4-8", 69.2],
    ["gpt-5-5", 58.6],
    ["gemini-3-1-pro", 54.2],
  ]),
  ...claimSet("openai-gpt-55", "swe-bench-pro", "Evaluations · Coding table", "same-table", [
    ["gpt-5-5", 58.6],
    ["gpt-5-4", 57.7],
    ["opus-4-7", 64.3],
    ["gemini-3-1-pro", 54.2],
  ]),
  ...claimSet("xai-grok-45", "terminal-bench-2-1", "Terminal Bench 2.1 chart", "reported-best", [
    ["fable-5", 84.3],
    ["gpt-5-5", 83.4],
    ["grok-4-5", 83.3],
    ["opus-4-8", 78.9],
    ["opus-4-7", 78.9],
  ]),
  ...claimSet("zai-glm-52", "terminal-bench-2-1", "Terminal Bench 2.1 · Best Reported Harness", "reported-best", [
    ["glm-5-2", 82.7, "Claude Code"],
    ["glm-5-1", 69, "Claude Code"],
    ["opus-4-8", 78.9, "Claude Code"],
    ["gpt-5-5", 83.4, "Codex"],
    ["gemini-3-1-pro", 70.7, "Gemini CLI"],
  ]),
  ...claimSet("xai-grok-45", "swe-marathon", "SWE Marathon chart", "reported-best", [
    ["grok-4-5", 29],
    ["opus-4-8", 26],
    ["fable-5", 24],
    ["opus-4-7", 16],
  ]),
  ...claimSet("zai-glm-52", "swe-marathon", "Full benchmark table · Coding · SWE-Marathon", "same-table", [
    ["glm-5-2", 13],
    ["glm-5-1", 1],
    ["opus-4-8", 26],
    ["gpt-5-5", 12],
    ["gemini-3-1-pro", 4],
  ]),
  ...claimSet("zai-glm-52", "hle", "Full benchmark table · Reasoning · HLE", "same-table", [
    ["glm-5-2", 40.5],
    ["glm-5-1", 31],
    ["qwen-3-7-max", 41.4],
    ["minimax-m3", 37],
    ["deepseek-v4-pro", 37.7],
    ["opus-4-8", 49.8, "Full-set value"],
    ["gpt-5-5", 41.4, "Full-set value"],
    ["gemini-3-1-pro", 45],
  ]),
  ...claimSet("zai-glm-52", "hle-tools", "Full benchmark table · Reasoning · HLE w/ Tools", "same-table", [
    ["glm-5-2", 54.7],
    ["glm-5-1", 52.3],
    ["qwen-3-7-max", 53.5],
    ["deepseek-v4-pro", 48.2],
    ["opus-4-8", 57.9, "Full-set value"],
    ["gpt-5-5", 52.2, "Full-set value"],
    ["gemini-3-1-pro", 51.4, "Full-set value"],
  ]),
];

export const prices: Price[] = [
  {
    modelId: "kimi-k3",
    sourceId: "kimi-k3-release",
    input: 3,
    cachedInput: 0.3,
    output: 15,
    condition: "Official Kimi API · 1M-token context · cache-miss input shown as standard input",
    effectiveAt: "2026-07-16",
  },
  {
    modelId: "gpt-5-5",
    sourceId: "openai-gpt-55-pricing",
    input: 5,
    cachedInput: 0.5,
    output: 30,
    condition: "Standard API, ≤272K input tokens",
    effectiveAt: dataThrough,
  },
  {
    modelId: "fable-5",
    sourceId: "anthropic-pricing",
    input: 10,
    cachedInput: 1,
    cacheWrite5m: 12.5,
    cacheWrite1h: 20,
    output: 50,
    condition: "Claude API standard rates",
    effectiveAt: dataThrough,
  },
  {
    modelId: "opus-4-8",
    sourceId: "anthropic-pricing",
    input: 5,
    cachedInput: 0.5,
    cacheWrite5m: 6.25,
    cacheWrite1h: 10,
    output: 25,
    condition: "Claude API standard rates",
    effectiveAt: dataThrough,
  },
  {
    modelId: "gemini-3-1-pro",
    sourceId: "google-pricing",
    input: 2,
    cachedInput: 0.2,
    output: 12,
    webSearchPerThousand: 14,
    condition: "Paid tier, prompts ≤200K tokens",
    effectiveAt: "2026-06-18",
  },
  {
    modelId: "grok-4-5",
    sourceId: "xai-pricing",
    input: 2,
    cachedInput: 0.3,
    output: 6,
    condition: "Standard context, <200K tokens",
    effectiveAt: dataThrough,
  },
];

export const labById = new Map(labs.map((lab) => [lab.id, lab]));
export const modelById = new Map(models.map((model) => [model.id, model]));
export const benchmarkById = new Map(benchmarks.map((benchmark) => [benchmark.id, benchmark]));
export const sourceById = new Map(sources.map((source) => [source.id, source]));
